// The template registry (2026-11-02,
// docs/decisions/2026-11-02-template-stable-keys.md): every Templates
// library row the code names, by a stable key, with the titles it has today
// as aliases.
//
// A template's title is staff-editable in Settings → Templates. Its key is
// not: it is code-managed, set by migration 0171 from the titles below, and
// kept when the template is renamed. Code finds a template by its key inside
// the folder root it asks for ("1A eBay", "AM Website", ...), and only when
// no row in that root carries the key, by the title alias (the old lookup),
// with a warning in the log (server/src/repositories/templates.js).
//
// Scope: one key names one logical template. The same key is used in every
// library that has that template ("refund_confirmation" is "Refund
// Confirmation (eBay)" in "1A eBay/Refund" and "Refund Confirmation" in "AM
// Website/Refund"); the folder root keeps the brand and channel variants
// apart. In the database a key is unique per folder root.
//
// Entry: { folder, titles: { ebay?, website?, support? } }.
//   folder  the sub-folder the code looks in ("Refund", "Pre Fit"; the
//           brands spell some differently, "Prefit" / "TRACKING", which the
//           lookups compare without case or spaces).
//   titles  per library, every spelling the code accepts, the first one
//           first. A library listed is one the template is expected in
//           (server/src/scripts/report-template-keys.js reports a gap).
//   gap     why a library the code asks never has it (known, kept).
//
// Adding a key: add it here, use it in code, and add a migration that sets it
// on the rows (like 0171). Until then the title alias finds it, with the
// warning. server/test/template-keys.test.js holds the guards.

const entry = (folder, titles, extra = {}) => Object.freeze({ folder, titles: Object.freeze(Object.fromEntries(Object.entries(titles).map(([k, v]) => [k, Object.freeze(v)]))), ...extra })

export const TEMPLATE_LIBRARIES = Object.freeze(['ebay', 'website', 'support'])

export const TEMPLATE_REGISTRY = Object.freeze({
  address_change_ebay_no_shipped: entry('Address Change', { ebay: ['Address Change eBay No Shipped'] }),

  cancel_confirmation: entry('Cancel', { ebay: ['Cancel Confirmation (eBay)'], website: ['Cancel Confirmation'] }),
  cancel_already_shipped: entry('Cancel', { ebay: ['Cancel eBay Already Shipped'], website: ['Cancel No Already Shipped'] }),
  cancel_for_fraud: entry('Cancel', { website: ['Cancel For Fraud'] }),
  cancel_for_fraud_ebay_alternative: entry('Cancel', { website: ['Cancel For Fraud - eBay Alternative', 'Cancel For Fraud – eBay Alternative', 'Cancel For Fraud — eBay Alternative'] }),

  cannot_locate_order: entry('Misc', { ebay: ['Cannot Locate Order - eBay'], website: ['Cannot Locate Order'] }),
  invoice_request: entry('Misc', { website: ['Invoice Request'] }),

  // actions.js's repeat-defect step; the draft is this template (2026-11-02).
  cdr_repeat_defect_60_days: entry('CDR', { ebay: ['CDR Repeat Defect within 60 days Information'] }),

  gen_ebay_how_to_return: entry('RMARTN', { ebay: ['Gen eBay How to Return'] }),

  // The pre-sale house style's sources (server/src/ai/house-style.js,
  // HOUSE_STYLE_SOURCE_KEYS; 0174): the VIN ask on a Website account comes
  // from its Fitment folder's VIN Needed template; the don't-carry wording
  // and the stock note from these Pre Inquiry templates.
  fit_vin_needed: entry('Fitment', { website: ['Fit VIN Needed'] }),
  pre_inquiry_includes_no: entry('Pre Inquiry', { ebay: ['Pre Inquiry Includes No (eBay)'], website: ['Pre Inquiry Includes No'] }),
  pre_inquiry_oos_no_eta: entry('Pre Inquiry', { ebay: ['Pre Inquiry OOS no ETA'] }),
  pre_inquiry_oos_notify_me: entry('Pre Inquiry', { ebay: ['Pre Inquiry OOS Notify Me'], website: ['Pre Inquiry OOS Notify Me'] }),
  // The listing rule's pre-sale templates (server/src/ai/listing-content.js,
  // LISTING_RULE_TEMPLATE_KEYS; 0174), with pre_inquiry_includes_no above.
  pre_inquiry_measurements_no: entry('Pre Inquiry', { ebay: ['Pre Inquiry Measurements No (eBay)'], website: ['Pre Inquiry Measurements No'] }),
  pre_inquiry_no_bulbs: entry('Pre Inquiry', { ebay: ['Pre Inquiry No Bulbs (eBay)'], website: ['Pre Inquiry No Bulbs'] }),
  pre_inquiry_painted_mirrors: entry('Pre Inquiry', { ebay: ['Pre Inquiry Painted Mirrors (eBay)'], website: ['Pre Inquiry Painted Mirrors'] }),
  pre_inquiry_manufacturer: entry('Pre Inquiry', { ebay: ['Pre Inquiry Manufacturer (eBay)'], website: ['Pre Inquiry Manufacturer'] }),
  pre_inquiry_installation_instructions_no: entry('Pre Inquiry', { ebay: ['Pre Inquiry Installation Instructions No'], website: ['Pre Inquiry Installation Instructions No'] }),

  label_company_pays: entry('Label', { website: ['Label Company Pays'] }),
  label_customer_pays: entry('Label', { website: ['Label Customer Pays'] }),
  label_issue: entry('Label', { ebay: ['Label Issue'] }),
  label_where_is_it: entry('Label', { ebay: ['Label Where is it'], website: ['Label Where is it'] }),

  order_inquiry_no_gsp: entry('Replace', { ebay: ['Order Inquiry No GSP'] }),
  policyno_outside_60: entry('Policy Questions', { ebay: ['Policyno Outside 60'] }),

  prefit_carry_yes: entry('Pre Fit', { ebay: ['Prefit Carry Yes'], website: ['Prefit Carry Yes'] }),
  prefit_carry_no: entry('Pre Fit', { ebay: ['Prefit Carry No eBay'], website: ['Prefit Carry No'] }),
  prefit_mirror_option: entry('Pre Fit', { ebay: ['Prefit Mirror Option'], website: ['Prefit Mirror Option'] }),
  prefit_mirror_options_no_vin: entry('Pre Fit', { ebay: ['Prefit Mirror Options No Vin'], website: ['Prefit Mirror Options No Vin'] }),
  prefit_modified_vehicle_guarantee_no: entry('Pre Fit', { ebay: ['Prefit Modified Vehicle Guarantee No'], website: ['Prefit Modified Vehicle Guarantee No'] }),
  prefit_no_do_carry: entry('Pre Fit', { ebay: ['Prefit No Do Carry'], website: ['Prefit No Do Carry'] }),
  prefit_no_dont_carry: entry('Pre Fit', { ebay: ['Prefit No Dont Carry'], website: ['Prefit No Dont Carry', 'Prefit No Don’t Carry'] }),
  prefit_vin_invalid: entry('Pre Fit', { ebay: ['Prefit VIN Invalid'], website: ['Prefit VIN Invalid'] }),
  prefit_vin_needed: entry('Pre Fit', { ebay: ['Prefit VIN Needed'], website: ['Prefit VIN Needed'] }),
  prefit_yes: entry('Pre Fit', { ebay: ['Prefit Yes'], website: ['Prefit Yes'] }),

  refund_check_address_confirmed: entry('Refund', { ebay: ['Refund Check - Address Confirmed(eBay/AMZ)', 'Refund Check - Address Confirmed'] }),
  refund_check_need_address: entry('Refund', { ebay: ['Refund Check - Need Address(eBay/AMZ)', 'Refund Check - Need Address'] }),
  refund_confirmation: entry('Refund', { ebay: ['Refund Confirmation (eBay)'], website: ['Refund Confirmation'] }),
  refund_delivered: entry('Refund', { ebay: ['Refund Delivered'], website: ['Refund Delivered'] }),
  refund_delivered_no_rma: entry('Refund', { website: ['Refund Delivered - No RMA', 'Refund Delivered — No RMA', 'Refund Delivered – No RMA'] }),
  refund_in_transit: entry('Refund', { ebay: ['Refund in Transit'], website: ['Refund in Transit'] }),
  refund_in_transit_no_rma: entry('Refund', { website: ['Refund in Transit - No RMA', 'Refund in Transit — No RMA', 'Refund in Transit – No RMA'] }),
  refund_lost_in_shipping: entry('Refund', { ebay: ['Refund Lost in Shipping'], website: ['Refund Lost in Shipping'] }),
  refund_no_return: entry('Refund', { ebay: ['Refund No Return'], website: ['Refund No Return'] }),

  // No library has it yet: the replacement path falls back to its own wording.
  replace_already_refunded: entry('Replace', { ebay: ['Replace Already Refunded'], website: ['Replace Already Refunded'] }, { gap: 'not in any library yet; pipeline.js falls back to its own wording' }),
  replace_company_pays: entry('Replace', { ebay: ['Replace Company Pays'], website: ['Replace Company Pays'] }),
  replace_lost_in_shipping: entry('Replace', { ebay: ['Replace Lost in Shipping'], website: ['Replace Lost in Shipping'] }),
  replace_more_info_needed: entry('Replace', { ebay: ['Replace More Info Needed'] }),
  replace_no_return: entry('Replace', { ebay: ['Replace No Return'], website: ['Replace No Return'] }),
  replace_warranty_70: entry('Replace', { ebay: ['Replace Warranty 70'], website: ['Replace Warranty 70'] }),

  rmartn_cust_pays: entry('RMARTN', { website: ['RMARTN Cust Pays'] }),
  rmartn_cust_pays_do_carry: entry('RMARTN', { website: ['RMARTN Cust Pays Do Carry'] }),
  rmartn_cust_pays_dont_carry: entry('RMARTN', { website: ["RMARTN Cust Pays Don't Carry", 'RMARTN Cust Pays Dont Carry', 'RMARTN Cust Pays Don’t Carry'] }),
  rmartn_cust_pays_more_info: entry('RMARTN', { website: ['RMARTN Cust Pays More Info'] }),
  rmartn_no_prepaid_label: entry('RMARTN', { website: ['RMARTN No Prepaid Label'] }),

  sales_hold_address: entry('Sales Hold', { ebay: ['Sales Hold Address'], website: ['Sales Hold Address'] }),
  sales_hold_all_set: entry('Sales Hold', { ebay: ['Sales Hold All Set'], website: ['Sales Hold All Set'] }),
  sales_hold_cant_ship_military: entry('Sales Hold', { ebay: ["Sales Hold Can't Ship Military"], website: ["Sales Hold Can't Ship Military"] }),
  sales_hold_weight: entry('Sales Hold', { ebay: ['Sales Hold Weight'], website: ['Sales Hold Weight'] }),

  signature: entry('Signature', { ebay: ['Signature'], website: ['Signature'] }),

  tracking_freight_forwarder_not_gsp: entry('Tracking', { ebay: ['Tracking Freight Forwarder (Not GSP)'] }),
  tracking_gsp_ff: entry('Tracking', { ebay: ['Tracking GSP FF'] }),
  tracking_info_eta: entry('Tracking', { ebay: ['Tracking Info & ETA'], website: ['Tracking Info & ETA'] }),
  tracking_lost_delivered: entry('Tracking', { ebay: ['Tracking Lost Delivered'], website: ['Tracking Lost Delivered'] }),
  tracking_lost_in_shipping: entry('Tracking', { ebay: ['Tracking Lost in Shipping'] }),
  tracking_wait_5: entry('Tracking', { ebay: ['Tracking Wait 5'], website: ['Tracking Wait 5'] }),
})

// The folder roots of each library. A template's root is the folder's first
// segment ("1A eBay/Refund" → "1A eBay").
export const TEMPLATE_LIBRARY_ROOTS = Object.freeze({
  ebay: Object.freeze(['1A eBay', 'AM eBay']),
  website: Object.freeze(['1A Website', 'AM Website']),
  support: Object.freeze(['1A Support', 'AM Support']),
})

export function templateFolderRoot(folder) {
  const s = String(folder ?? '')
  const i = s.indexOf('/')
  return (i < 0 ? s : s.slice(0, i)).trim() || null
}

// 'ebay' | 'website' | 'support' | null, from a folder's root.
export function templateLibraryOf(folder) {
  const root = templateFolderRoot(folder)
  if (!root) return null
  if (/ ebay$/i.test(root)) return 'ebay'
  if (/ website$/i.test(root)) return 'website'
  if (/ support$/i.test(root)) return 'support'
  return null
}

// Lower case, one space, one kind of dash and apostrophe: how a title is
// compared with the aliases.
export function normalizeTemplateTitle(title) {
  return String(title ?? '')
    .replace(/[’‘`´]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const KEY_BY_TITLE = new Map()
for (const [key, e] of Object.entries(TEMPLATE_REGISTRY)) {
  for (const titles of Object.values(e.titles)) {
    for (const t of titles) if (!KEY_BY_TITLE.has(normalizeTemplateTitle(t))) KEY_BY_TITLE.set(normalizeTemplateTitle(t), key)
  }
}

function entryOf(key) {
  const e = TEMPLATE_REGISTRY[key]
  if (!e) throw new Error(`Unknown template key "${key}" (shared/src/template-keys.js)`)
  return e
}

// Every alias of a key: the library's own first, then the others'.
export function templateTitles(key, library = null) {
  const e = entryOf(key)
  const own = library ? (e.titles[library] ?? []) : []
  return [...new Set([...own, ...Object.values(e.titles).flat()])]
}

// Only the library's own aliases ([] when the library has none).
export function libraryTemplateTitles(key, library) {
  return [...(entryOf(key).titles[library] ?? [])]
}

// The title the code asks for: the library's first alias, else the key's
// first alias (a library the template is not expected in).
export function templateTitle(key, library = null) {
  return templateTitles(key, library)[0]
}

// The { folder, title, key } a lookup takes (return-refund.js's
// resolveTemplate()). library: whose title alias; by default the folder's.
export function templateRef(key, folder, library = templateLibraryOf(folder)) {
  return { folder: folder ?? null, title: templateTitle(key, library), key }
}

// A title as the agent's find_templates search phrase: lower case, "VIN"
// kept ("Prefit VIN Invalid" → "prefit VIN invalid").
export function templateSearchPhrase(title) {
  return String(title ?? '').toLowerCase().replace(/\bvin\b/g, 'VIN')
}

// The key a title is an alias of, or null.
export function templateKeyForTitle(title) {
  return KEY_BY_TITLE.get(normalizeTemplateTitle(title)) ?? null
}

// A template row's key: its own (the database), else its title's.
export function templateKeyOf(template) {
  if (template && typeof template === 'object') return template.key || templateKeyForTitle(template.title)
  return templateKeyForTitle(template)
}

// The title a row's per-template rules read: the registry's alias for its
// key (so a renamed row keeps its rules), else its own title.
export function canonicalTemplateTitle(template) {
  const key = template?.key
  return key && TEMPLATE_REGISTRY[key] ? templateTitle(key, templateLibraryOf(template.folder)) : (template?.title ?? null)
}

// Whether a template row (or a title) is the template a key names.
export function isTemplate(template, key) {
  return templateKeyOf(template) === key
}
